<script setup lang="ts">
import { computed, inject, useTemplateRef } from "vue";

import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import type { ListItemVariant } from "@/components/ui/ListItem/ListItem.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { ClickableProps } from "@/composables/Props";

import { menuListConfigKey, menuListItemsContextKey } from "./injectKeys";

const elRef = useTemplateRef<HTMLElement>("elRef");

const props = defineProps<
  ClickableProps & {
    as?: "li" | "div" | "button";
    static?: boolean; // Non-interactive, but no visual different
    disabled?: boolean; // Non-interactive and looks 'disabled'
    selected?: boolean;
    variant?: ListItemVariant; // "cards" variant has no set height
    badge?: string; // Optional badge text (e.g., "2/3 selected")
    tooltip?: string; // Tooltip message shown on hover
    role?: string; // ARIA role override
  }
>();

const listConfig = inject(menuListConfigKey, undefined);
const itemsContext = inject(menuListItemsContextKey, undefined);

const itemRole = computed(() => {
  if (props.role) return props.role;
  if (itemsContext?.role === "menu") return "menuitem";
  return "option";
});

defineExpose({ focus: () => void elRef.value?.focus() });
</script>

<template>
  <component
    :is="as || 'li'"
    ref="elRef"
    :class="[
      'menu-list-item',
      {
        static,
        disabled,
        selectable: listConfig?.selectable,
        selected,
        multiselect: listConfig?.multiselect,
      },
    ]"
    :role="itemRole"
    :aria-selected="!!selected"
    :aria-disabled="!!(static || disabled)"
    tabindex="-1"
  >
    <BaseTooltip :content="tooltip" position="right" class="tooltip-wrapper" show-when-clipped>
      <div class="wrapper">
        <div v-if="$slots.before" class="before">
          <slot name="before" />
        </div>
        <div v-else-if="icon" class="before">
          <SvgIcon :name="icon" />
        </div>

        <div :class="['content', variant || 'default']">
          <slot v-if="$slots.default" />
          <span v-else>{{ text }}</span>
        </div>

        <div v-if="$slots.after" class="after">
          <slot name="after" />
        </div>
        <div v-else-if="shortcut" class="after">
          <span class="btn">{{ shortcut }}</span>
        </div>

        <template v-if="listConfig?.multiselect && listConfig?.selectable">
          <SvgIcon
            v-if="selected"
            class="selected-icon"
            name="system/check"
            width="20"
            height="20"
          />
          <span v-else-if="badge" class="badge">{{ badge }}</span>
        </template>
      </div>
    </BaseTooltip>
  </component>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/mixins/utils";
@use "@/styles/mixins/typography";
@use "@/styles/mixins/shortcut";

.menu-list-item {
  flex-shrink: 0;
  outline: revert;
  padding: 2px 4px;

  // Tooltip wrapper should not affect layout (override BaseTooltip's inline-block)
  :deep(.tooltip-wrapper.base-tooltip) {
    display: block;
    width: 100%;
  }

  &.static {
    pointer-events: none;
  }

  &.disabled {
    cursor: not-allowed;
    color: colors.$text-disabled;

    :deep(.tooltip-wrapper.base-tooltip) {
      cursor: not-allowed;
    }

    .wrapper {
      cursor: not-allowed;
      color: colors.$text-disabled;

      .before,
      .after {
        color: colors.$text-disabled;
      }
    }
  }

  .wrapper {
    cursor: pointer;
    height: 36px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    border-radius: 4px;
    overflow: hidden;
    color: colors.$text;

    .before,
    .after {
      color: colors.$text-subtle;
      flex-shrink: 0;
      min-width: 20px;
      min-height: 20px;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .btn {
      @include shortcut.key;
    }
  }

  :focus-visible,
  &:hover:not(.disabled) {
    .wrapper {
      background-color: colors.$background-grey;

      .before,
      .after {
        color: colors.$text;
      }
    }
  }

  &:active:not(.multiselect, .disabled) {
    .wrapper {
      background-color: colors.$main;
      color: colors.$text-inverse;

      .before,
      .after {
        color: colors.$text-inverse;
      }
    }
  }

  &.selected.selectable:not(.multiselect) {
    .wrapper {
      background-color: colors.$main;
      color: colors.$text-inverse;

      .before,
      .after {
        color: colors.$text-inverse;
      }
    }
  }

  .content {
    @include utils.ellipsis;
    @include typography.medium;
    @include utils.ellipsis;

    display: block;
    flex-grow: 1;
  }

  .selected-icon {
    color: colors.$main;
    flex-shrink: 0;
  }

  .badge {
    @include typography.small;

    padding: 2px 4px;
    margin-right: 4px;
    color: colors.$text-subtle;
    background-color: colors.$background-grey-active;
    border-radius: 4px;
  }
}
</style>
